import axios from 'axios'
import {AUTH_TOKEN, APP_VERSION, FEATURE_FLAGS, GOOGLE_SYNC_NOTICE_SEEN} from "@utils/storageKeys";
import moment from "moment"
import localforage from "localforage";
import dispatchActionForAllModules from "@utils/dispatch-action-for-all-modules";
import {getSavedState, saveState} from "@utils/util";
import Vue from 'vue'
import { queryClient } from '@src/queries/client';

export const state = {
  currentUser: getSavedState('auth.currentUser'),
  userSmsSettings: null,
  token: getSavedState(AUTH_TOKEN),
  mobileToken: null,
  passcodeToken: getSavedState('auth.passcodeToken'),
};

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue;
    saveState('auth.currentUser', newValue);
    setDefaultAuthHeaders(state);
  },

  SET_CURRENT_USER_LOGO(state, value){
    state.currentUser.logo = value;
    saveState('auth.currentUser', state.currentUser);
  },

  SET_CURRENT_USER_PROFILE_IMAGE(state, value){
    state.currentUser.image = value;
    saveState('auth.currentUser', state.currentUser);
  },

  SET_PURCHASE_STATE_FALSE(state){
    state.currentUser.isPremium = false;
    saveState('auth.currentUser', state.currentUser)
  },
  SET_PURCHASE_STATE_TRUE(state){
    state.currentUser.isPremium = true;
    saveState('auth.currentUser', state.currentUser)
  },

  UPDATE_SUBSCRIPTION_DATE(state, value){
    state.currentUser.subscriptionDue = value;
    saveState('auth.currentUser', state.currentUser)
  },

  SET_CURRENT_USER_TOKEN(state, newValue) {
    state.token = newValue;
    saveState(AUTH_TOKEN, newValue);
    setDefaultAuthHeaders(state);
  },

  SET_MOBILE_TOKEN(state, token){
    state.mobileToken = token;
    saveState('mobile.token', token);
    setDefaultAuthHeaders(state);
  },

  UPDATE_CURRENT_USER_COMPANY(state, newValue) {
    state.currentUser.userCompany = newValue;
    saveState('auth.currentUser', state.currentUser)
  },

  ENABLE_USER_EMAIL(state){
    state.currentUser.isActivated = true;
    saveState('auth.currentUser', state.currentUser);
  },

  UPDATE_CURRENT_USER_TO_DOS(state, newValue) {
    state.currentUser.userToDo.unshift(newValue);
    saveState('auth.currentUser', state.currentUser)
  },
  SWITCH_CURRENT_USER_TO_DO(state, todoId) {
    const foundIndex = state.currentUser.userToDo.findIndex(
        (x) => x.id === todoId
    );
    state.currentUser.userToDo[foundIndex].isCompleted = !state.currentUser.userToDo[foundIndex].isCompleted;
    saveState('auth.currentUser', state.currentUser);
  },

  DELETE_CURRENT_USER_TO_DO(state, todoId) {
    state.currentUser.userToDo = state.currentUser.userToDo.filter(
        (x) => x.id !== todoId
    );
    saveState('auth.currentUser', state.currentUser)
  },

  SET_USER_SMS_SETTINGS(state, value){
    state.userSmsSettings = value;
  },

  SET_PASSCODE_TOKEN(state, token) {
    state.passcodeToken = token;
    saveState('auth.passcodeToken', token);
    setDefaultAuthHeaders(state);
  },

  SET_HAS_PASSCODE(state, value) {
    if (state.currentUser) {
      Vue.set(state.currentUser, 'hasPasscode', value);
      saveState('auth.currentUser', state.currentUser);
    }
  }
};

export const getters = {
  loggedIn(state) {
    return !!state.currentUser && !!state.token && state.token.length > 0
  },

  isStudent(state) {
    return state.currentUser?.tier === 'student';
  },

  hasPasscode(state) {
    return !!state.currentUser?.hasPasscode;
  },

  validPasscodeToken(state) {
    if (!state.passcodeToken) return null;
    try {
      const payload = JSON.parse(atob(state.passcodeToken.split('.')[1]));
      if (payload.exp * 1000 > Date.now()) return state.passcodeToken;
    } catch (e) { /* malformed token */ }
    return null;
  },
};

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, getters, dispatch }) {
    setDefaultAuthHeaders(state);
    dispatch('validate');
    if (!getters.loggedIn) return;
    dispatch('generateUserMobileToken');
    dispatch('refetchUser');
    dispatch('settings/fetchSettings', null, { root: true });
  },
  cacheUser({commit}, data){
    commit('SET_CURRENT_USER', data.user);
    commit('SET_CURRENT_USER_TOKEN', data.token);
  },

  // Logs out the current user.
  logOut({ commit }) {
    dispatchActionForAllModules('reset');
    queryClient.clear();
    let version = localStorage.getItem(APP_VERSION);
    const kept = [FEATURE_FLAGS, GOOGLE_SYNC_NOTICE_SEEN].map((key) => [key, localStorage.getItem(key)]);
    localStorage.clear();
    localStorage.setItem(APP_VERSION, version);
    kept.forEach(([key, value]) => value && localStorage.setItem(key, value));
    localforage.clear().then();
  },

  updateUser({ commit, dispatch }, newUser) {
    return axios.put(`${process.env.VUE_APP_API}/users/`, newUser).then((response) => {
      return dispatch('refetchUser');
    })
  },

  generateUserMobileToken({commit}) {
    return axios.get(`${process.env.VUE_APP_API}/mobile/token`).then(res=>{
      commit('SET_MOBILE_TOKEN', res.data);
      return res.data;
    }).catch();
  },

  updateUserLogo({commit}, logoUrl){
    return axios.post(`${process.env.VUE_APP_API}/users/logo`, {logo: logoUrl}).then((res) => {
      commit('SET_CURRENT_USER_LOGO', logoUrl);
      return res;
    })
  },

  updateUserImage({commit}, image){
    commit('SET_CURRENT_USER_PROFILE_IMAGE', image);
  },

  refetchUser({ commit, dispatch }){
    return axios.get(process.env.VUE_APP_API + `/users/`).then(result => {
      commit('SET_CURRENT_USER', result.data.user);
      dispatch('fetchUserSmsSettings');
      return result;
    })
  },

  fetchUserSmsSettings({ commit }) {
    return axios.get(process.env.VUE_APP_API + `/users/sms/settings`).then(result => {
      commit('SET_USER_SMS_SETTINGS', result.data);
      return result.data;
    });
  },

  verifyEmail({commit}){
    commit('ENABLE_USER_EMAIL');
  },

  updateUserCompany({ commit }, userCompany) {
    return axios
        .put(`${process.env.VUE_APP_API}/users/user-company`, userCompany)
        .then((response) => {
          commit('UPDATE_CURRENT_USER_COMPANY', userCompany);
          return response.data
        })
  },

  addToDo({ commit }, toDo) {
    return axios.post(`${process.env.VUE_APP_API}/users/todos`, toDo).then((response) => {
      commit('UPDATE_CURRENT_USER_TO_DOS', response.data);
      return response.data
    })
  },

  switchToDoStatus({ commit }, { id }) {
    return axios.put(`${process.env.VUE_APP_API}/users/todo/` + id).then((response) => {
      commit('SWITCH_CURRENT_USER_TO_DO', id);
      return response.data
    })
  },

  deleteUserToDo({ commit }, { id }) {
    return axios.delete(`${process.env.VUE_APP_API}/users/todo/` + id).then((response) => {
      commit('DELETE_CURRENT_USER_TO_DO', id);
      return response.data
    })
  },

  // register the user
  register({ commit, dispatch, getters }, { firstName, lastName, email, password, phone } = {}) {
    if (getters.loggedIn) return dispatch('validate');

    return axios.post(`${process.env.VUE_APP_API}/users/register`, {firstName, lastName, email, password, phone}).then((response) => {
      return response.data
    })
  },

  // register the user
  resetPassword({ commit, dispatch, getters }, { email } = {}) {
    if (getters.loggedIn) return dispatch('validate');

    return axios.post(`${process.env.VUE_APP_API}/users/forgot-password`, { email }).then((response) => {
      return response.data
    })
  },

  // Validates the current user's token and logs out if it isn't good!
  validate({ commit, state }) {
    // @todo: CHECK HERE. SOMETHING SEEMS WRONG!
    if (!state.currentUser){
      commit('SET_CURRENT_USER', null);
      commit('SET_CURRENT_USER_TOKEN', null);
      return Promise.resolve(null);
    }
    return state.currentUser;

  },

  notPurchasedState({commit, state}){
    commit('SET_PURCHASE_STATE_FALSE', null);
    return state.currentUser;
  },

  purchasedState({commit, state}){
    commit('SET_PURCHASE_STATE_TRUE', null);
    return state.currentUser;
  },

  validateAdmin({state}){
    if (!state.currentUser) return Promise.resolve(null);

    return axios.get(process.env.VUE_APP_API + `/users/is-valid-admin`);

  },

  updateSubscriptionDueDate({commit, state}){
    let dueDate = moment().add(1, 'year')
    commit('UPDATE_SUBSCRIPTION_DATE', dueDate);
    return state.currentUser;
  },

  // --- Passcode ---

  setPasscode({ commit }, { passcode }) {
    return axios.post(`${process.env.VUE_APP_API}/users/passcode`, { passcode }).then(() => {
      commit('SET_HAS_PASSCODE', true);
    });
  },

  verifyPasscode({ commit }, { passcode }) {
    return axios.post(`${process.env.VUE_APP_API}/users/passcode/verify`, { passcode }).then(res => {
      commit('SET_PASSCODE_TOKEN', res.data.passcodeToken);
      return res.data.passcodeToken;
    });
  },

  changePasscode(_, { currentPasscode, newPasscode }) {
    return axios.put(`${process.env.VUE_APP_API}/users/passcode`, { currentPasscode, newPasscode });
  },

  requestPasscodeRemoval() {
    return axios.post(`${process.env.VUE_APP_API}/users/passcode/request-removal`);
  },

  confirmPasscodeRemoval({ commit }, { token }) {
    return axios.post(`${process.env.VUE_APP_API}/users/passcode/confirm-removal`, { token }).then(() => {
      commit('SET_HAS_PASSCODE', false);
      commit('SET_PASSCODE_TOKEN', null);
    });
  },

  clearPasscodeToken({ commit }) {
    commit('SET_PASSCODE_TOKEN', null);
  },

  reset({commit}){
    commit('SET_CURRENT_USER', null);
    commit('SET_CURRENT_USER_TOKEN', null);
    commit('SET_MOBILE_TOKEN', null);
    commit('SET_PASSCODE_TOKEN', null);
  }
};

// ===
// Private helpers
// ===

function getValidPasscodeToken(state) {
  if (!state.passcodeToken) return '';
  try {
    const payload = JSON.parse(atob(state.passcodeToken.split('.')[1]));
    if (payload.exp * 1000 > Date.now()) return state.passcodeToken;
  } catch (e) { /* malformed */ }
  return '';
}

function setDefaultAuthHeaders(state) {
  const passcodeHeader = getValidPasscodeToken(state);
  const globalVueAxios = Vue.prototype.$axios;
  if(globalVueAxios){
    globalVueAxios.defaults.headers['x-access-token'] = state.token || ''
    globalVueAxios.defaults.headers['x-dietitian-token'] = state.mobileToken || ''
    globalVueAxios.defaults.headers['x-passcode-token'] = passcodeHeader
  }
  axios.defaults.headers['x-access-token'] = state.token || ''
  axios.defaults.headers['x-dietitian-token'] = state.mobileToken || ''
  axios.defaults.headers['x-passcode-token'] = passcodeHeader
}
